// The character controller (decision 0044): a kinematic controller on Rapier's character controller, through
// `@rpg/collision`, stepped by the game inside its fixed sim tick. The controller holds no state of its own: it steps a
// plain `ControllerState` the game keeps (as a kernel component), so saves, replays and hashes cover it. It owns the
// body's character settings (slopes, steps, snap, sliding, from the tuning) and its movement modes.
import type { BodyId, CharacterMove, CollisionWorld, Vec3 } from "@rpg/collision";
import { airMode, defaultRules, glideMode, groundMode, ruleLeaves, type ControllerFaults, type ModeContext, type ModeRule, type MovementMode } from "./modes";
import { restState, type ControllerInput, type ControllerState, type ModeId } from "./state";
import { checkTuning, deriveRates, type ControllerRates, type ControllerTuning } from "./tuning";

/** What a surface tag does to a controller standing on it. */
export interface SurfaceRule {
  /** Target speed × this. Default 1. */
  speed?: number;
}

export interface ControllerOptions {
  world: CollisionWorld;
  /** A kinematic body with a capsule collider sized by the tuning (the game adds it). */
  body: BodyId;
  tuning: ControllerTuning;
  /** The fixed step (s). Default the world's. */
  dt?: number;
  /** The modes the game turns on. Default ground, air and glide. Ground and air are required. */
  modes?: readonly MovementMode[];
  /** Extra rules, checked before the built-in ones. */
  rules?: readonly ModeRule[];
  /** Surface tag → what it does (the game's table). */
  surfaces?: Readonly<Record<string, SurfaceRule>>;
  /** Surface tags a climb mode may climb. */
  climbTags?: readonly string[];
}

/** One mode change, for traces and specs. */
export interface ModeChange {
  from: ModeId;
  to: ModeId;
  rule: string;
}

export interface Controller {
  readonly body: BodyId;
  readonly tuning: ControllerTuning;
  readonly rates: ControllerRates;
  /** Planted defects (tests only). */
  readonly faults: ControllerFaults;
  /** The registered modes' IDs. */
  modes(): ModeId[];
  /** A state standing on `feet` (the capsule one skin above) facing `yaw`, with the body put there. */
  spawn(feet: Vec3, yaw?: number): ControllerState;
  /** Teleport a state and its body: at rest, standing on `feet` (one skin above), in the ground mode. */
  warp(s: ControllerState, feet: Vec3, yaw?: number): void;
  /** Change tuning values live (a dev tuning panel): checked as a whole first (nothing changes and the problems come
   *  back when the result fails `checkTuning`), then the step rates are derived again. Movement values act on the next
   *  step; the body's character settings (skin, slopes, steps, snap) were given to the collision world at creation and
   *  only change when the controller is built again. */
  retune(changes: Partial<ControllerTuning>): string[];
  /** One fixed step. Mutates `s`; returns the mode changes it made (usually none). */
  step(s: ControllerState, input: ControllerInput, dt?: number): ModeChange[];
  /** The body's centre for a state's feet. */
  centreOf(feet: Vec3): Vec3;
  /** The last step's move (what it touched, grounded or not): a view of the step just run, not state. Null before
   *  the first step and after a step whose mode placed the body itself. */
  lastMove(): CharacterMove | null;
}

const DEG = Math.PI / 180;

/** Build a controller over a body. Registers the body as a character with the collision world. Throws on a tuning
 *  `checkTuning` rejects or a missing ground or air mode. */
export function createController(o: ControllerOptions): Controller {
  const problems = checkTuning(o.tuning);
  if (problems.length) throw new Error(`controller: bad tuning: ${problems.join("; ")}`);
  const t = { ...o.tuning };
  const dt0 = o.dt ?? o.world.dt;
  let rates = deriveRates(t, dt0);
  const modeList = o.modes ?? [groundMode(), airMode(), glideMode()];
  const modes = new Map<ModeId, MovementMode>(modeList.map((m) => [m.id, m]));
  if (!modes.has("ground") || !modes.has("air")) throw new Error("controller: the ground and air modes are required");
  const rules = [...(o.rules ?? []), ...defaultRules()];
  const surfaces = o.surfaces ?? {};
  const climbTags = o.climbTags ?? [];
  const faults: ControllerFaults = { noCoyote: false };
  const lift = t.halfHeight + t.radius;
  const world = o.world;
  let last: CharacterMove | null = null;

  world.addCharacter(o.body, {
    skin: t.skin,
    maxClimbAngle: t.maxSlope * DEG,
    minSlideAngle: t.maxSlope * DEG,
    autostep: { maxHeight: t.stepHeight, minWidth: t.stepMinWidth },
    snapToGround: t.snapDistance,
    slide: true,
  });

  const centreOf = (feet: Vec3): Vec3 => ({ x: feet.x, y: feet.y + lift, z: feet.z });

  function context(s: ControllerState, input: ControllerInput, dt: number): ModeContext {
    const surf = s.surface !== null ? (surfaces[s.surface]?.speed ?? 1) : 1;
    const wade = s.water ? 1 + (t.wadeSpeedScale - 1) * Math.min(1, s.water.depth / t.wadeDepth) : 1;
    const c: ModeContext = {
      s,
      input,
      t,
      r: rates,
      dt,
      world,
      body: o.body,
      faults,
      speedScale: surf * wade * (input.scale?.move ?? 1),
      move: null,
      climbTags,
      jumpSpent: false,
      hasMode: (id) => modes.has(id),
      heightAbove(max) {
        const hit = world.groundUnder(o.body, max, centreOf(s.feet));
        const ground = hit ? hit.distance : Infinity;
        const w = world.waterSurface(s.feet.x, s.feet.z);
        const water = w !== null && w <= s.feet.y ? s.feet.y - w : Infinity;
        const h = Math.min(ground, water);
        return h > max ? Infinity : h;
      },
      place(feet) {
        s.feet = { ...feet };
        world.moveKinematic(o.body, centreOf(feet));
      },
    };
    return c;
  }

  function runRules(c: ModeContext, when: "input" | "contact", changes: ModeChange[]): void {
    const s = c.s;
    for (const rule of rules) {
      if (rule.when !== when || !ruleLeaves(rule, s.mode) || rule.to === s.mode || !modes.has(rule.to)) continue;
      if (!rule.test(c)) continue;
      const from = s.mode;
      modes.get(from)?.exit?.(c, rule.to);
      s.mode = rule.to;
      s.modeTime = 0;
      modes.get(rule.to)!.enter?.(c, from);
      changes.push({ from, to: rule.to, rule: rule.name });
      return;
    }
  }

  return {
    body: o.body,
    tuning: t,
    retune(changes) {
      const next = { ...t, ...changes };
      const problems = checkTuning(next);
      if (problems.length) return problems;
      Object.assign(t, changes);
      rates = deriveRates(t, rates.dt);
      return [];
    },
    get rates() {
      return rates;
    },
    faults,
    modes: () => [...modes.keys()],
    centreOf,
    lastMove: () => last,
    // a spawn or warp stands the capsule one skin above `feet`, where Rapier's controller keeps it: put exactly on
    // the ground, its first move reads as not grounded (measured: a fall and a landing on the step after a warp)
    spawn(feet, yaw = 0) {
      const f = { x: feet.x, y: feet.y + t.skin, z: feet.z };
      const s = restState(f, yaw);
      world.setPose(o.body, centreOf(f));
      return s;
    },
    warp(s, feet, yaw = s.yaw) {
      const f = { x: feet.x, y: feet.y + t.skin, z: feet.z };
      Object.assign(s, restState(f, yaw));
      world.setPose(o.body, centreOf(f));
    },
    step(s, input, dt = dt0) {
      if (dt !== rates.dt) rates = deriveRates(t, dt);
      const changes: ModeChange[] = [];
      if (s.buffer >= 0) s.buffer += dt;
      const c = context(s, input, dt);
      runRules(c, "input", changes);
      const mode = modes.get(s.mode);
      if (!mode) throw new Error(`controller: no mode "${s.mode}"`);
      const desired = mode.step(c);
      last = null;
      if (desired) {
        const m = world.moveCharacter(o.body, desired);
        (c as { move: typeof m }).move = m;
        last = m;
        // the feet follow the body (where the move sends it), so the state never drifts from Rapier's body
        const b = world.pose(o.body).position;
        s.feet = { x: b.x + m.moved.x, y: b.y + m.moved.y - lift, z: b.z + m.moved.z };
        s.grounded = m.grounded;
        // what is underfoot (probed where the move sends the body) and the water the feet are in
        const under = m.grounded ? world.groundUnder(o.body, t.snapDistance, centreOf(s.feet)) : null;
        s.surface = under?.tag ?? null;
        s.ground = under?.id ?? null;
      } else {
        s.grounded = false;
        s.surface = null;
        s.ground = null;
      }
      const w = world.waterAt({ x: s.feet.x, y: s.feet.y + 0.01, z: s.feet.z });
      s.water = w ? { surface: w.surface, depth: w.depth } : null;
      if (s.water && s.surface === null && s.grounded) s.surface = w!.tag;
      mode.after?.(c);
      runRules(c, "contact", changes);
      s.modeTime += dt;
      return changes;
    },
  };
}
